import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  AdminParameterOverride,
  ParameterGroup,
  HandleType,
  UIComponentType,
  ValidationType,
} from "@genieartinc/schemas";

export interface ParameterInfo {
  name: string;
  type: string;
  title?: string;
  description?: string;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  enum?: unknown[];
  required?: boolean;
}

interface CollapsibleParameterRowProps {
  paramInfo: ParameterInfo;
  override?: AdminParameterOverride;
  group: ParameterGroup;
  isMainOutput?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (override: AdminParameterOverride) => void;
  onMainOutputChange?: (isMain: boolean) => void;
}

const HANDLE_TYPES: { value: HandleType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
  { value: "mask", label: "Mask" },
  { value: "file", label: "File" },
  { value: "3d", label: "3D" },
  { value: "code", label: "Code" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "any", label: "Any" },
];

const UI_COMPONENT_TYPES: { value: UIComponentType; label: string }[] = [
  { value: "slider", label: "Slider" },
  { value: "select", label: "Select" },
  { value: "input", label: "Input" },
  { value: "textarea", label: "Textarea" },
  { value: "toggle", label: "Toggle" },
  { value: "number", label: "Number" },
  { value: "select-or-input", label: "Select or Input" },
  { value: "multi-select", label: "Multi-Select" },
  { value: "color", label: "Color Picker" },
  { value: "seed", label: "Seed (Special)" },
];

const VALIDATION_TYPES: { value: ValidationType; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "seed", label: "Seed (0-2147483647)" },
  { value: "positive-integer", label: "Positive Integer" },
  { value: "email", label: "Email" },
  { value: "url", label: "URL" },
];

const GROUP_COLORS: Record<ParameterGroup, { bg: string; text: string; label: string }> = {
  input: { bg: "bg-blue-500/10", text: "text-blue-700", label: "Input" },
  output: { bg: "bg-green-500/10", text: "text-green-700", label: "Output" },
  settings: { bg: "bg-purple-500/10", text: "text-purple-700", label: "Settings" },
};

export function CollapsibleParameterRow({
  paramInfo,
  override = {},
  group,
  isMainOutput = false,
  isExpanded,
  onToggle,
  onChange,
  onMainOutputChange,
}: CollapsibleParameterRowProps) {
  const handleOverrideChange = (updates: Partial<AdminParameterOverride>) => {
    onChange({ ...override, ...updates });
  };

  const groupColor = GROUP_COLORS[group];

  // Collapsed view
  if (!isExpanded) {
    return (
      <div
        className="rounded-lg border bg-card hover:bg-accent/5 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 p-2">
          {/* Parameter Name */}
          <code className="text-xs font-mono font-semibold flex-shrink-0">
            {paramInfo.name}
          </code>

          {/* Badges */}
          <div className="flex items-center gap-1 flex-wrap">
            <Badge variant="outline" className={`text-[10px] ${groupColor.bg} ${groupColor.text} border-0`}>
              {groupColor.label}
            </Badge>

            {paramInfo.required && (
              <Badge variant="destructive" className="text-[10px]">
                required
              </Badge>
            )}

            {isMainOutput && (
              <Badge variant="default" className="text-[10px] bg-amber-500">
                ⭐ Main
              </Badge>
            )}

            {/* Hidden/Disabled badges for settings group */}
            {group === "settings" && override.hidden && (
              <Badge variant="secondary" className="text-[10px] bg-orange-500/10 text-orange-700 border-orange-500/20">
                hidden
              </Badge>
            )}

            {group === "settings" && override.disabled && (
              <Badge variant="secondary" className="text-[10px] bg-gray-500/10 text-gray-700 border-gray-500/20">
                disabled
              </Badge>
            )}

            <span className="text-[10px] text-muted-foreground">
              [{paramInfo.type}]
            </span>

            {override.uiTypeOverride && (
              <span className="text-[10px] text-muted-foreground">
                • {override.uiTypeOverride}
              </span>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Toggle Icon */}
          <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="rounded-lg border bg-card">
      {/* Header (clickable to collapse) */}
      <div
        className="flex items-center gap-2 p-2 border-b cursor-pointer hover:bg-accent/5 transition-colors"
        onClick={onToggle}
      >
        <code className="text-xs font-mono font-semibold">{paramInfo.name}</code>

        <Badge variant="outline" className={`text-[10px] ${groupColor.bg} ${groupColor.text} border-0`}>
          {groupColor.label}
        </Badge>

        {paramInfo.required && (
          <Badge variant="destructive" className="text-[10px]">
            required
          </Badge>
        )}

        {isMainOutput && (
          <Badge variant="default" className="text-[10px] bg-amber-500">
            ⭐ Main Output
          </Badge>
        )}

        <div className="flex-1" />

        <ChevronUp className="h-3 w-3 text-muted-foreground" />
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 divide-x">
        {/* LEFT: Parameter Info (Read-only) */}
        <div className="p-3 space-y-2">
          <div>
            {paramInfo.title && (
              <p className="text-xs font-medium text-muted-foreground mb-0.5">
                {paramInfo.title}
              </p>
            )}
            {paramInfo.description && (
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {paramInfo.description}
              </p>
            )}
          </div>

          <Separator />

          <div className="space-y-1 text-[11px]">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground w-14">Type:</span>
              <code className="text-foreground font-mono">{paramInfo.type}</code>
            </div>

            {paramInfo.default !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-14">Default:</span>
                <code className="text-foreground font-mono text-[10px]">
                  {JSON.stringify(paramInfo.default)}
                </code>
              </div>
            )}

            {(paramInfo.minimum !== undefined || paramInfo.maximum !== undefined) && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-14">Range:</span>
                <code className="text-foreground font-mono">
                  {paramInfo.minimum ?? "−∞"} to {paramInfo.maximum ?? "+∞"}
                </code>
              </div>
            )}

            {paramInfo.enum && (
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground w-14 flex-shrink-0">Enum:</span>
                <div className="flex flex-wrap gap-0.5">
                  {paramInfo.enum.slice(0, 5).map((val, idx) => (
                    <Badge key={idx} variant="secondary" className="text-[10px] font-mono">
                      {String(val)}
                    </Badge>
                  ))}
                  {paramInfo.enum.length > 5 && (
                    <Badge variant="outline" className="text-[10px]">
                      +{paramInfo.enum.length - 5}
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Override Settings (Group-specific) */}
        <div className="p-3 space-y-3">
          <h4 className="text-xs font-semibold">Override Settings</h4>

          {/* INPUT GROUP SETTINGS */}
          {group === "input" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-[11px]">Input Type</Label>
                <Select
                  value={override.inputType || "file"}
                  onValueChange={(value) => handleOverrideChange({ inputType: value as HandleType })}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HANDLE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-xs">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px]">Min Handles</Label>
                  <Input
                    type="number"
                    min={0}
                    value={override.minHandles ?? 0}
                    onChange={(e) =>
                      handleOverrideChange({ minHandles: parseInt(e.target.value) || 0 })
                    }
                    className="h-7 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Max Handles</Label>
                  <Input
                    type="number"
                    min={1}
                    value={override.maxHandles ?? 1}
                    onChange={(e) =>
                      handleOverrideChange({ maxHandles: parseInt(e.target.value) || 1 })
                    }
                    className="h-7 text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px]">Validation</Label>
                <Select
                  value={override.validationType || "default"}
                  onValueChange={(value) =>
                    handleOverrideChange({ validationType: value as ValidationType })
                  }
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VALIDATION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-xs">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* OUTPUT GROUP SETTINGS */}
          {group === "output" && (
            <>
              {onMainOutputChange && (
                <div className="flex items-center gap-2">
                  <Switch
                    id={`${paramInfo.name}-mainOutput`}
                    checked={isMainOutput}
                    onCheckedChange={onMainOutputChange}
                    className={isMainOutput ? "data-[state=checked]:bg-amber-500" : ""}
                  />
                  <Label htmlFor={`${paramInfo.name}-mainOutput`} className="text-xs cursor-pointer">
                    Mark as main output (generated asset)
                  </Label>
                </div>
              )}

              <Separator />

              <div className="space-y-1.5">
                <Label className="text-[11px]">Output Mode</Label>
                <Select
                  value={override.outputMode || "json"}
                  onValueChange={(value) =>
                    handleOverrideChange({ outputMode: value as "handle" | "json" })
                  }
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="handle" className="text-xs">
                      Handle (right side)
                    </SelectItem>
                    <SelectItem value="json" className="text-xs">
                      JSON Data (metadata)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {override.outputMode === "handle" && (
                <div className="space-y-1.5">
                  <Label className="text-[11px]">Output Type</Label>
                  <Select
                    value={override.outputType || "file"}
                    onValueChange={(value) =>
                      handleOverrideChange({ outputType: value as HandleType })
                    }
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HANDLE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-xs">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {/* NODE SETTINGS GROUP SETTINGS */}
          {group === "settings" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium">Visibility</Label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Switch
                      id={`${paramInfo.name}-hidden`}
                      checked={override.hidden ?? false}
                      onCheckedChange={(checked) => handleOverrideChange({ hidden: checked })}
                    />
                    <Label htmlFor={`${paramInfo.name}-hidden`} className="text-[11px] cursor-pointer">
                      Hidden
                    </Label>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Switch
                      id={`${paramInfo.name}-disabled`}
                      checked={override.disabled ?? false}
                      onCheckedChange={(checked) => handleOverrideChange({ disabled: checked })}
                    />
                    <Label htmlFor={`${paramInfo.name}-disabled`} className="text-[11px] cursor-pointer">
                      Disabled
                    </Label>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <Label className="text-[11px]">UI Component</Label>
                <Select
                  value={override.uiTypeOverride || "auto"}
                  onValueChange={(value) =>
                    handleOverrideChange({
                      uiTypeOverride: value === "auto" ? undefined : (value as UIComponentType),
                    })
                  }
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue placeholder="Auto-detect" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto" className="text-xs italic">
                      Auto-detect from schema
                    </SelectItem>
                    {UI_COMPONENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-xs">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium">Value Overrides</Label>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Default Value</Label>
                    <Input
                      value={override.defaultOverride !== undefined ? String(override.defaultOverride) : ""}
                      onChange={(e) =>
                        handleOverrideChange({
                          defaultOverride: e.target.value === "" ? undefined : e.target.value,
                        })
                      }
                      placeholder="Leave empty for model default"
                      className="h-7 text-xs"
                    />
                  </div>

                  {(paramInfo.type === "number" || paramInfo.type === "integer") && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Min Override</Label>
                        <Input
                          type="number"
                          value={override.minOverride !== undefined ? override.minOverride : ""}
                          onChange={(e) =>
                            handleOverrideChange({
                              minOverride: e.target.value === "" ? undefined : parseFloat(e.target.value),
                            })
                          }
                          placeholder={String(paramInfo.minimum ?? "−∞")}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Max Override</Label>
                        <Input
                          type="number"
                          value={override.maxOverride !== undefined ? override.maxOverride : ""}
                          onChange={(e) =>
                            handleOverrideChange({
                              maxOverride: e.target.value === "" ? undefined : parseFloat(e.target.value),
                            })
                          }
                          placeholder={String(paramInfo.maximum ?? "+∞")}
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <Label className="text-[11px]">Validation</Label>
                <Select
                  value={override.validationType || "default"}
                  onValueChange={(value) =>
                    handleOverrideChange({ validationType: value as ValidationType })
                  }
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VALIDATION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-xs">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px]">Admin Note</Label>
                <Input
                  value={override.note || ""}
                  onChange={(e) => handleOverrideChange({ note: e.target.value })}
                  placeholder="Why this override?"
                  className="h-7 text-xs"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
